import usePartners from 'components/Partners/hooks/usePartners';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  EEntityOwnership,
  EFilterType,
  IFilterComponentProps,
  TEntityOwnership,
} from './filter.types';
import { Skeleton } from '@mui/material';
import FilterAutocompleteSelection, {
  IFilterAutocompleteSelectItem,
} from 'components/Filter/FilterAutocompleteSelection';

const OwnershipFilter: FC<IFilterComponentProps<EFilterType.OWNERSHIP>> = ({ value, onChange }) => {
  const [selectedOwnership, setSelectedOwnership] = useState<string | undefined>(
    value?.option === EEntityOwnership.PARTNER && value.ownedById
      ? `${EEntityOwnership.PARTNER}_${value.ownedById}`
      : value?.option || undefined
  );

  const { partners, loading } = usePartners();

  const items: IFilterAutocompleteSelectItem[] = useMemo(
    () => [
      { id: EEntityOwnership.INTERNAL, title: 'My company', displayFirst: true },
      { id: EEntityOwnership.EXTERNAL, title: 'Owned by partners', displayFirst: true },
      { id: EEntityOwnership.UNASSIGNED, title: 'Unassigned', displayFirst: true },
      ...partners.map(({ id, name }) => ({
        id: `${EEntityOwnership.PARTNER}_${id}`,
        title: name,
      })),
    ],
    [partners]
  );

  /** Listen to the changes */
  useEffect(
    () =>
      setSelectedOwnership(
        value?.option === EEntityOwnership.PARTNER && value.ownedById
          ? `${EEntityOwnership.PARTNER}_${value.ownedById}`
          : value?.option || undefined
      ),
    [value]
  );

  if (loading) {
    return <Skeleton variant="rounded" width="100%" height={30} />;
  }

  const handleSelectionChange = (selectedItems?: string | string[]) => {
    const newSelection = selectedItems as string; // Single select
    setSelectedOwnership(newSelection);
    if (!newSelection) {
      onChange({ option: null });
    } else if (newSelection.includes(EEntityOwnership.PARTNER)) {
      const partnerId = newSelection.split('_')[1];
      onChange({ option: EEntityOwnership.PARTNER, ownedById: partnerId });
    } else {
      onChange({ option: newSelection as TEntityOwnership });
    }
  };

  return (
    <FilterAutocompleteSelection
      label="Ownership"
      placeholder="All partners"
      items={items}
      selected={selectedOwnership}
      onSelectionChange={handleSelectionChange}
    />
  );
};

export default OwnershipFilter;
